import { z } from "zod";
import {
  DISCORD_EPOCH_MS,
  DISCORD_MAX_CHANNELS,
  DISCORD_MAX_ROLES,
  DISCORD_SNOWFLAKE,
  GRANTABLE_PERMISSION_NAMES,
} from "./discord.ts";
import type { GrantablePermission } from "./discord.ts";
import { standardEmojiSchema } from "./message-template.ts";
import { VOICE_STATES } from "./voice.ts";
import {
  CURVE_EXPRESSION_MAX_LENGTH,
  MAX_XP,
  curveTotals,
  readCurveExpression,
} from "./curve-expression.ts";
import { highestAtMost } from "./highest-at-most.ts";
import {
  messageTemplateSchema,
  pictureOverLimit,
  reorderingTexts,
  templateShowsText,
} from "./message-template.ts";
import type { MessageTemplate, TemplateRefusal } from "./message-template.ts";
import { TEXT_REORDERS, nameProblem } from "./names.ts";
import {
  CARD_LAYOUTS,
  CARD_MODES,
  cardDesignSchema,
  currentCardDesign,
  isBuiltInCard,
} from "./card-design.ts";
import type { CardDesign, CardLook } from "./card-design.ts";
import { memberCardPartsSchema, memberCardsSchema } from "./member-card.ts";
import { personalRolePartsSchema } from "./personal-role.ts";
import type { PersonalRolePart } from "./personal-role.ts";
import type { MemberCardPart } from "./member-card.ts";
import type { ImageUse } from "./image-library.ts";
import { reachesCeiling } from "./prestige.ts";
import { windowDaysSchema } from "./time-window.ts";
import type { WindowDays } from "./time-window.ts";

export const LEVELING_METRIC_FAMILIES = ["text", "voice", "commands", "emoji", "apps"] as const;

export type LevelingMetricFamily = (typeof LEVELING_METRIC_FAMILIES)[number];

export const LEVELING_METRIC_FAMILY = {
  messages: "text",
  active_minutes: "text",
  words: "text",
  chars: "text",
  attachments: "text",
  stickers: "text",
  links: "text",
  gif_messages: "text",
  reactions_given: "text",
  reactions_received: "text",
  mentions_made: "text",
  mentions_received: "text",
  replies: "text",
  replies_received: "text",
  voice_minutes: "voice",
  streaming_minutes: "voice",
  video_minutes: "voice",
  command_invocations: "commands",
  emoji_sent: "emoji",
  app_minutes: "apps",
} as const satisfies Record<string, LevelingMetricFamily>;

export type LevelingMetric = keyof typeof LEVELING_METRIC_FAMILY;

export const LEVELING_METRICS = Object.keys(LEVELING_METRIC_FAMILY) as readonly LevelingMetric[];

const CHANNEL_SCOPED_FAMILIES: readonly LevelingMetricFamily[] = ["text", "voice", "commands"];

export const isChannelScoped = (metric: LevelingMetric): boolean =>
  CHANNEL_SCOPED_FAMILIES.includes(LEVELING_METRIC_FAMILY[metric]);

export const CAP_PERIODS = ["hour", "day", "week"] as const;

export type CapPeriod = (typeof CAP_PERIODS)[number];

export const ANNOUNCE_MODES = ["off", "same_channel", "channel", "dm"] as const;

export type AnnounceMode = (typeof ANNOUNCE_MODES)[number];

const COMMAND_VISIBILITIES = ["public", "ephemeral"] as const;

const LEVELING_CONFLICTS = [
  "import_running",
  "source_unavailable",
  "run_not_file",
  "run_reading",
  "run_not_reading",
  "switched_already",
  "not_switched",
  "leveling_off",
] as const;

export type LevelingConflict = (typeof LEVELING_CONFLICTS)[number];

export const MAX_FORMULA_TERMS = 24;

export const MAX_MULTIPLIERS = 24;

const WESTMOST_OFFSET_MS = 12 * 3_600_000;

export const multiplierEnded = (rule: Pick<Multiplier, "until">, now: Date = new Date()): boolean =>
  rule.until !== null &&
  boundDay(rule.until) < new Date(now.getTime() - WESTMOST_OFFSET_MS).toISOString().slice(0, 10);

export const liveRuleCounts = (
  formula: {
    readonly terms: readonly { readonly paused?: true | undefined }[];
    readonly multipliers: readonly Pick<Multiplier, "paused" | "until">[];
  },
  now: Date = new Date(),
): { readonly terms: number; readonly multipliers: number } => ({
  terms: formula.terms.filter((term) => term.paused !== true).length,
  multipliers: formula.multipliers.filter(
    (rule) => rule.paused !== true && !multiplierEnded(rule, now),
  ).length,
});

export const MAX_MULTIPLIER_FACTOR = 100;

export const MIN_MULTIPLIER_FACTOR = 1;

const PG_INTEGER_MAX = 2 ** 31 - 1;

export const MAX_LEVEL = 100_000;

const MAX_WEIGHT = 10_000;

const amount = z.number().int().min(1).max(MAX_XP);

export const FILE_IMPORT_LIMIT = 10_000;

export const MAX_REWARDS = DISCORD_MAX_ROLES;

export const MAX_REQUIRES = 4;

export const MAX_CARRIES = DISCORD_MAX_ROLES;

const DAY_MS = 86_400_000;

const withinDiscordsLifetime = (days: number): boolean =>
  days * DAY_MS <= Date.now() - DISCORD_EPOCH_MS;

export const MAX_MANAGED_CHANNELS = DISCORD_MAX_CHANNELS;

const snowflake = z.string().regex(DISCORD_SNOWFLAKE, "is not a Discord id");

const roleIds = z.array(snowflake).max(DISCORD_MAX_ROLES);

const channelIds = z.array(snowflake).max(DISCORD_MAX_CHANNELS);

const day = z.iso.date();

const bound = z.union([day, z.iso.datetime({ local: true, precision: -1 })]);

export const boundDay = (value: string): string => value.slice(0, 10);

export const boundTimed = (value: string): boolean => value.length > 10;

const boundStart = (value: string): string => (boundTimed(value) ? value : `${value}T00:00`);

const boundEnd = (value: string): string => (boundTimed(value) ? value : `${value}T24:00`);

const weekday = z.number().int().min(1).max(7);

const hour = z.number().int().min(0).max(23);

const channelScopeSchema = z.strictObject({ channels: channelIds, categories: channelIds });

export type ChannelScope = z.infer<typeof channelScopeSchema>;

const appKey = z.string().trim().min(1).max(128);

export const APP_FILTER_MODES = ["only", "except"] as const;

export type AppFilterMode = (typeof APP_FILTER_MODES)[number];

const appFilterSchema = z.strictObject({
  mode: z.enum(APP_FILTER_MODES),
  keys: z.array(appKey).min(1),
});

const capSchema = z.strictObject({
  per: z.enum(CAP_PERIODS),
  xp: amount,
});

export const FORMULA_REFUSALS = ["curve_unreadable", "curve_ceiling", "weight_range"] as const;

export type FormulaRefusal = (typeof FORMULA_REFUSALS)[number];

const formulaTermSchema = z
  .strictObject({
    metric: z.enum(LEVELING_METRICS as [LevelingMetric, ...LevelingMetric[]]),
    weight: z.number().min(0).max(MAX_WEIGHT),
    weightMax: z.number().min(0).max(MAX_WEIGHT).optional(),
    include: channelScopeSchema.optional(),
    exclude: channelScopeSchema.optional(),
    voiceStates: z.array(z.enum(VOICE_STATES)).min(1).optional(),
    countAlone: z.boolean().optional(),
    apps: appFilterSchema.optional(),
    cap: capSchema.optional(),
    paused: z.literal(true).optional(),
  })
  .refine((term) => term.weightMax === undefined || term.weightMax > term.weight, {
    message: "the top of a range must be above its bottom",
    params: { reason: "weight_range" satisfies FormulaRefusal },
  })
  .refine(
    (term) =>
      isChannelScoped(term.metric) || (term.include === undefined && term.exclude === undefined),
    { message: "this metric has no channel, so it cannot be scoped to channels" },
  )
  .refine(
    (term) =>
      LEVELING_METRIC_FAMILY[term.metric] === "voice" ||
      (term.voiceStates === undefined && term.countAlone === undefined),
    { message: "voice states and alone time only apply to voice metrics" },
  )
  .refine((term) => LEVELING_METRIC_FAMILY[term.metric] === "apps" || term.apps === undefined, {
    message: "activities only apply to app metrics",
  });

export type FormulaTerm = z.infer<typeof formulaTermSchema>;

const formulaFiltersSchema = z.strictObject({
  exclude: channelScopeSchema,
  excludeRoles: roleIds,
  onlyRoles: roleIds,
});

export type FormulaFilters = z.infer<typeof formulaFiltersSchema>;

const multiplierSchema = z
  .strictObject({
    factor: z.number().min(0).max(MAX_MULTIPLIER_FACTOR),
    channels: channelIds,
    categories: channelIds,
    roles: roleIds,
    weekdays: z.array(weekday).max(7),
    hours: z.array(hour).max(24),
    from: bound.nullable(),
    until: bound.nullable(),
    newcomerDays: z
      .number()
      .int()
      .min(1)
      .refine(withinDiscordsLifetime, { message: "is longer than Discord has existed" })
      .optional(),
    paused: z.literal(true).optional(),
  })
  .refine(
    (rule) =>
      rule.from === null || rule.until === null || boundStart(rule.from) < boundEnd(rule.until),
    { message: "from must not be after until" },
  );

export type Multiplier = z.infer<typeof multiplierSchema>;

const checkpointsFit = (levels: readonly number[], totals: readonly number[]): boolean =>
  levels.length === totals.length &&
  levels.every((level, index) => {
    const before = index === 0 ? 0 : levels[index - 1]!;
    const beforeTotal = index === 0 ? 0 : totals[index - 1]!;
    return level > before && totals[index]! - beforeTotal >= level - before;
  });

const tableCurveSchema = z
  .strictObject({
    kind: z.literal("table"),
    thresholds: z
      .array(z.number().int().min(1))
      .min(1)
      .max(1_000)
      .refine(
        (values) => values.every((value, index) => index === 0 || value > values[index - 1]!),
        { message: "each threshold must exceed the one before it" },
      ),
    levels: z.array(z.number().int().min(1).max(MAX_LEVEL)).min(1).max(1_000).optional(),
  })
  .refine((table) => table.levels === undefined || checkpointsFit(table.levels, table.thresholds), {
    message: "each checkpoint's level must rise, by no more levels than its XP rises",
    path: ["levels"],
  });

const expressionCurveSchema = z.strictObject({
  kind: z.literal("expression"),
  total: z
    .string()
    .min(1)
    .max(CURVE_EXPRESSION_MAX_LENGTH)
    .refine((total) => readCurveExpression(total).ok, {
      message: "the formula cannot be read",
      params: { reason: "curve_unreadable" satisfies FormulaRefusal },
    })
    .refine((total) => curveTotals(total, MAX_LEVEL) !== null, {
      message: "each level must take at least 1 XP more than the one before",
    })
    .refine(
      (total) => {
        const totals = curveTotals(total, MAX_LEVEL);
        return totals === null || totals.length > 1;
      },
      {
        message: "the formula's first level must take no more XP than can be counted",
        params: { reason: "curve_ceiling" satisfies FormulaRefusal },
      },
    ),
});

const curveSchema = z.discriminatedUnion("kind", [tableCurveSchema, expressionCurveSchema]);

export type Curve = z.infer<typeof curveSchema>;

export const MAX_PRESTIGE = PG_INTEGER_MAX;

const PRESTIGE_GROWTH_LIMITS = [0, 1_000] as const;

const PRESTIGE_CEILING_LIMITS = [1, 100] as const;

export const prestigeSchema = z.strictObject({
  growth: z.number().int().min(PRESTIGE_GROWTH_LIMITS[0]).max(PRESTIGE_GROWTH_LIMITS[1]),
  ceiling: z
    .number()
    .min(PRESTIGE_CEILING_LIMITS[0])
    .max(PRESTIGE_CEILING_LIMITS[1])
    .refine((ceiling) => Math.round(ceiling * 10) / 10 === ceiling, {
      message: "the ceiling takes at most one decimal",
    }),
});

export type PrestigeRule = z.infer<typeof prestigeSchema>;

export const formulaSchema = z
  .strictObject({
    terms: z.array(formulaTermSchema).min(1),
    filters: formulaFiltersSchema,
    multipliers: z.array(multiplierSchema),
    curve: curveSchema,
    maxLevel: z.number().int().min(1).max(MAX_LEVEL).nullable(),
    prestige: prestigeSchema.nullable().optional(),
  })
  .refine((formula) => liveRuleCounts(formula).terms <= MAX_FORMULA_TERMS, {
    message: `at most ${MAX_FORMULA_TERMS} sources are switched on at once`,
    path: ["terms"],
  })
  .refine((formula) => liveRuleCounts(formula).multipliers <= MAX_MULTIPLIERS, {
    message: `at most ${MAX_MULTIPLIERS} multipliers are switched on and not yet over at once`,
    path: ["multipliers"],
  })
  .refine(
    (formula) =>
      formula.prestige === undefined || formula.prestige === null || formula.maxLevel !== null,
    { message: "prestige starts at the highest level, so it needs one", path: ["prestige"] },
  )
  .refine(reachesCeiling, {
    message:
      "the prestige ceiling is out of reach: the climbs up to it cost more XP in all than a member can hold",
    path: ["prestige", "ceiling"],
  });

export type Formula = z.infer<typeof formulaSchema>;

export const liveFormula = (formula: Formula): Formula => ({
  ...formula,
  terms: formula.terms.filter((term) => term.paused !== true),
  multipliers: formula.multipliers.filter((rule) => rule.paused !== true),
});

export const ANNOUNCE_WHEN = ["every", "reward", "nth"] as const;

export const announceWhenSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("every") }),
  z.strictObject({ kind: z.literal("reward") }),
  z.strictObject({ kind: z.literal("nth"), n: z.number().int().min(2).max(MAX_LEVEL) }),
]);

export type AnnounceWhen = z.infer<typeof announceWhenSchema>;

const hexColor = z.string().regex(/^#[0-9a-f]{6}$/i, "is not a #rrggbb colour");

const cardLookSchema = z.strictObject({
  layout: z.enum(CARD_LAYOUTS),
  accent: hexColor,
  mode: z.enum(CARD_MODES),
}) satisfies z.ZodType<CardLook>;

export const cardChoiceSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("look"), look: cardLookSchema }),
  z.strictObject({ kind: z.literal("image"), imageId: z.uuid() }),
]);

export type CardChoice = z.infer<typeof cardChoiceSchema>;

const announceSchema = z
  .strictObject({
    mode: z.enum(ANNOUNCE_MODES),
    channelId: snowflake.nullable(),
    template: messageTemplateSchema.nullable(),
    when: announceWhenSchema,
    card: cardChoiceSchema.optional(),
  })
  .refine((announce) => announce.mode !== "channel" || announce.channelId !== null, {
    message: "a fixed announcement channel needs a channel",
  });

const unique = <T>(values: readonly T[]): boolean => new Set(values).size === values.length;

export const REWARD_STACKING = ["highest", "stack"] as const;

export type RewardStacking = (typeof REWARD_STACKING)[number];

const MANAGED_ROLE_MODES = ["create", "adopt"] as const;

const DRIFT_POLICIES = ["revert", "flag"] as const;

const grantable = z.enum(
  GRANTABLE_PERMISSION_NAMES as [GrantablePermission, ...GrantablePermission[]],
);

const grantables = z.array(grantable).max(GRANTABLE_PERMISSION_NAMES.length);

const channelAccessSchema = z
  .strictObject({
    targetId: snowflake,
    allow: grantables,
    deny: grantables.min(1).optional(),
  })
  .refine((access) => access.allow.length > 0 || access.deny !== undefined, {
    message: "a channel entry allows or denies at least one permission",
    path: ["allow"],
  })
  .refine((access) => !access.deny?.some((name) => access.allow.includes(name)), {
    message: "a permission is either allowed or denied in a channel",
    path: ["deny"],
  });

export type ChannelAccess = z.infer<typeof channelAccessSchema>;

export const unitedChannelAccess = (entries: readonly ChannelAccess[]): ChannelAccess[] => {
  const merged = new Map<
    string,
    { allowed: Set<GrantablePermission>; denied: Set<GrantablePermission> }
  >();
  for (const entry of entries) {
    let target = merged.get(entry.targetId);
    if (target === undefined) {
      target = { allowed: new Set(), denied: new Set() };
      merged.set(entry.targetId, target);
    }
    for (const name of entry.allow) target.allowed.add(name);
    for (const name of entry.deny === undefined ? [] : entry.deny) target.denied.add(name);
  }
  return [...merged].map(([targetId, { allowed, denied }]) => {
    const deny = GRANTABLE_PERMISSION_NAMES.filter(
      (name) => denied.has(name) && !allowed.has(name),
    );
    return {
      targetId,
      allow: GRANTABLE_PERMISSION_NAMES.filter((name) => allowed.has(name)),
      ...(deny.length === 0 ? {} : { deny }),
    };
  });
};

export const ROLE_STYLES = ["solid", "gradient", "holographic"] as const;

export type RoleStyleKind = (typeof ROLE_STYLES)[number];

export const roleStyleSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("solid") }),
  z.strictObject({ kind: z.literal("gradient"), secondary: hexColor }),
  z.strictObject({ kind: z.literal("holographic") }),
]);

export type RoleStyle = z.infer<typeof roleStyleSchema>;

export const HOLOGRAPHIC_COLORS = {
  primary: "#a9c9ff",
  secondary: "#ffbbec",
  tertiary: "#ffc3a0",
} as const;

const roleAppearanceSchema = z
  .strictObject({
    name: z.string().trim().min(1).max(100),
    color: hexColor.nullable(),
    style: roleStyleSchema.optional(),
    hoist: z.boolean(),
    mentionable: z.boolean(),
  })
  .refine((look) => look.style?.kind !== "gradient" || look.color !== null, {
    message: "a gradient starts from the role's colour",
    path: ["color"],
  });

export const ROLE_ICON_EDGE = 128;

export const ROLE_ICON_MAX_BYTES = 256 * 1024;

export const ROLE_ICON_TYPES = ["image/png", "image/webp"] as const;

export const roleIconSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    kind: z.literal("emoji"),
    emoji: standardEmojiSchema,
  }),
  z.strictObject({ kind: z.literal("image"), assetId: z.uuid() }),
  z.strictObject({ kind: z.literal("none") }),
]);

export type RoleIcon = z.infer<typeof roleIconSchema>;

export const personalRoleLookSchema = z
  .strictObject({
    name: z.string().trim().min(1).max(100),
    color: hexColor.nullable(),
    style: roleStyleSchema,
    icon: roleIconSchema,
    hoist: z.boolean(),
  })
  .refine((look) => look.style.kind !== "gradient" || look.color !== null, {
    message: "a gradient starts from the role's colour",
    path: ["color"],
  });

export type PersonalRoleLook = z.infer<typeof personalRoleLookSchema>;

export const personalRolePartsUsed = (look: PersonalRoleLook): readonly PersonalRolePart[] => [
  "name",
  ...(look.color === null ? [] : (["color"] as const)),
  ...(look.style.kind === "gradient" ? (["gradient"] as const) : []),
  ...(look.style.kind === "holographic" ? (["holographic"] as const) : []),
  ...(look.icon.kind === "none" ? [] : (["icon"] as const)),
  ...(look.hoist ? (["hoist"] as const) : []),
];

const managedRoleSchema = z
  .strictObject({
    mode: z.enum(MANAGED_ROLE_MODES),
    roleId: snowflake.nullable(),
    appearance: roleAppearanceSchema.nullable(),
    permissions: z.array(grantable).max(GRANTABLE_PERMISSION_NAMES.length),
    channels: z.array(channelAccessSchema).max(MAX_MANAGED_CHANNELS),
    icon: roleIconSchema.optional(),
  })
  .refine((role) => (role.mode === "adopt") === (role.roleId !== null), {
    message: "an adopted role names an existing role; a created one does not",
  })
  .refine((role) => role.mode !== "create" || role.appearance !== null, {
    message: "a created role needs a name and look",
  });

export type ManagedRole = z.infer<typeof managedRoleSchema>;

const prestigeCount = z.number().int().max(MAX_PRESTIGE);

const roleTriggerSchema = z.strictObject({
  kind: z.literal("role"),
  roleId: snowflake,
});

export const rewardTriggerSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    kind: z.literal("level"),
    level: z.number().int().min(1).max(MAX_LEVEL),
    prestige: prestigeCount.min(0).optional(),
  }),
  z.strictObject({ kind: z.literal("prestige"), prestige: prestigeCount.min(1) }),
  z.strictObject({
    kind: z.literal("placing"),
    windowDays: windowDaysSchema,
    top: z.number().int().min(1),
    metric: z.enum(LEVELING_METRICS as [LevelingMetric, ...LevelingMetric[]]).optional(),
  }),
  z.strictObject({
    kind: z.literal("activity"),
    windowDays: windowDaysSchema,
    minXp: amount,
  }),
  z.strictObject({
    kind: z.literal("stat"),
    metric: z.enum(LEVELING_METRICS as [LevelingMetric, ...LevelingMetric[]]),
    windowDays: windowDaysSchema,
    min: amount,
    keep: z.literal(true).optional(),
    apps: z.array(appKey).min(1).readonly().optional(),
  }),
  z.strictObject({
    kind: z.literal("tenure"),
    days: z
      .number()
      .int()
      .min(1)
      .refine(withinDiscordsLifetime, { message: "is longer than Discord has existed" }),
  }),
  z.strictObject({ kind: z.literal("invites"), min: amount }),
  roleTriggerSchema,
]);

export type RewardTrigger = z.infer<typeof rewardTriggerSchema>;

const requirementSchema = rewardTriggerSchema.refine(
  (trigger) =>
    trigger.kind !== "level" &&
    trigger.kind !== "prestige" &&
    !(trigger.kind === "stat" && trigger.keep === true),
  { message: "a perk's further condition is an achievement, a stay, invites or a role held" },
);

const OFF_LADDER_KINDS = ["placing", "activity", "stat", "tenure", "invites", "role"] as const;

export type OffLadderTrigger = Extract<
  RewardTrigger,
  { readonly kind: (typeof OFF_LADDER_KINDS)[number] }
>;

export const isOffLadder = (trigger: RewardTrigger): trigger is OffLadderTrigger =>
  (OFF_LADDER_KINDS as readonly string[]).includes(trigger.kind);

export const windowOfTrigger = (trigger: RewardTrigger): WindowDays | undefined =>
  trigger.kind === "placing" || trigger.kind === "activity" || trigger.kind === "stat"
    ? trigger.windowDays
    : undefined;

const ON_LADDER_KINDS: readonly unknown[] = ["level", "prestige"] satisfies RewardTrigger["kind"][];

export const readRolesOf = (reward: Pick<Reward, "trigger" | "requires">): readonly string[] =>
  [reward.trigger, ...requiresOf(reward)].flatMap((trigger) =>
    trigger.kind === "role" ? [trigger.roleId] : [],
  );

type OwnThen = Pick<
  Reward,
  "trigger" | "grant" | "cardEditing" | "levelUpImageId" | "personalRole"
>;

const handsOutOwn = (reward: OwnThen): boolean =>
  reward.grant !== undefined ||
  isCardReward(reward) ||
  reward.levelUpImageId !== undefined ||
  reward.personalRole !== undefined;

export const perkCount = (items: readonly OwnThen[]): number => {
  const counted = items.filter(handsOutOwn);
  return (
    counted.filter((item) => item.trigger.kind !== "role").length +
    new Set(counted.flatMap((item) => (item.trigger.kind === "role" ? [item.trigger.roleId] : [])))
      .size
  );
};

export const carriesOf = (reward: {
  readonly carries?: readonly string[] | undefined;
}): readonly string[] => (reward.carries === undefined ? [] : reward.carries);

type Carrying = Pick<Reward, "id" | "trigger" | "carries">;

const perksNamed = <R extends Carrying>(items: readonly R[]): ((id: string) => readonly R[]) => {
  const byId = new Map(items.map((item) => [item.id, item]));
  const byRole = new Map<string, R[]>();
  for (const item of items) {
    if (item.trigger.kind !== "role") continue;
    byRole.set(item.trigger.roleId, [...(byRole.get(item.trigger.roleId) ?? []), item]);
  }
  return (id) => {
    const item = byId.get(id);
    if (item === undefined) return [];
    const group = item.trigger.kind === "role" ? byRole.get(item.trigger.roleId) : undefined;
    return group === undefined ? [item] : group;
  };
};

export const carriedClosures = <R extends Carrying>(
  items: readonly R[],
): ReadonlyMap<string, readonly R[]> => {
  const named = perksNamed(items);
  return new Map(
    items.map((item) => {
      const reached = new Map<string, R>();
      const queue = [...carriesOf(item)];
      for (let next = queue.pop(); next !== undefined; next = queue.pop()) {
        for (const carried of named(next)) {
          if (reached.has(carried.id)) continue;
          reached.set(carried.id, carried);
          queue.push(...carriesOf(carried));
        }
      }
      reached.delete(item.id);
      return [item.id, [...reached.values()]];
    }),
  );
};

export const carriersOf = <R extends Carrying>(
  items: readonly R[],
): ((rewardId: string) => readonly R[]) => {
  const carriers = new Map<string, R[]>();
  const byId = new Map(items.map((item) => [item.id, item]));
  for (const [carrierId, carried] of carriedClosures(items)) {
    const carrier = byId.get(carrierId);
    if (carrier === undefined) continue;
    for (const item of carried) carriers.set(item.id, [...(carriers.get(item.id) ?? []), carrier]);
  }
  return (rewardId) => carriers.get(rewardId) ?? [];
};

export const carriesAccessFrom = (reward: Pick<Reward, "trigger" | "requires">): boolean =>
  reward.trigger.kind === "role" && reward.requires === undefined;

export const mayCarry = (reward: Pick<Reward, "trigger" | "requires" | "grant">): boolean =>
  carriesAccessFrom(reward) || reward.grant?.kind === "managed_role";

export interface CarriedAccess {
  readonly permissions: readonly GrantablePermission[];
  readonly channels: readonly ChannelAccess[];
}

export const ownAccessOf = (reward: Pick<Reward, "grant">): CarriedAccess | null => {
  const { grant } = reward;
  if (grant?.kind === "access") return { permissions: grant.permissions, channels: grant.channels };
  if (grant?.kind === "managed_role") {
    return { permissions: grant.role.permissions, channels: grant.role.channels };
  }
  return null;
};

export const unitedAccess = (access: readonly CarriedAccess[]): CarriedAccess => {
  const held = new Set(access.flatMap((entry) => entry.permissions));
  return {
    permissions: GRANTABLE_PERMISSION_NAMES.filter((name) => held.has(name)),
    channels: unitedChannelAccess(access.flatMap((entry) => entry.channels)),
  };
};

export const carriedAccessOf = (items: readonly Reward[]): ReadonlyMap<string, CarriedAccess> =>
  new Map(
    [...carriedClosures(items)].flatMap(([carrierId, carried]): [string, CarriedAccess][] => {
      const reached = new Set(carried);
      const access = items.flatMap((item) => {
        if (!reached.has(item)) return [];
        const own = ownAccessOf(item);
        return own === null ? [] : [own];
      });
      return access.length === 0 ? [] : [[carrierId, unitedAccess(access)]];
    }),
  );

export const requiresOf = (reward: Pick<Reward, "requires">): readonly OffLadderTrigger[] =>
  reward.requires === undefined ? [] : reward.requires.filter(isOffLadder);

const appsFitMetric = (trigger: RewardTrigger): boolean =>
  trigger.kind !== "stat" ||
  trigger.apps === undefined ||
  LEVELING_METRIC_FAMILY[trigger.metric] === "apps";

export const accessRoleOf = (reward: Pick<Reward, "trigger" | "grant">): string | null =>
  reward.trigger.kind === "role" && reward.grant?.kind === "access" ? reward.trigger.roleId : null;

const rewardGrantSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("role"), roleId: snowflake }),
  z.strictObject({ kind: z.literal("managed_role"), role: managedRoleSchema }),
  z.strictObject({
    kind: z.literal("access"),
    permissions: z.array(grantable).max(GRANTABLE_PERMISSION_NAMES.length),
    channels: z.array(channelAccessSchema).max(MAX_MANAGED_CHANNELS),
  }),
]);

export type RewardGrant = z.infer<typeof rewardGrantSchema>;

interface CardEditing {
  readonly cardEditing?: readonly MemberCardPart[] | undefined;
}

export const rewardIdSchema = z.string().regex(/^[a-z0-9][a-z0-9-]{3,31}$/, "is not a reward id");

const rewardSchema = z
  .strictObject({
    id: rewardIdSchema,
    trigger: rewardTriggerSchema,
    requires: z.array(requirementSchema).min(1).max(MAX_REQUIRES).optional(),
    carries: z.array(rewardIdSchema).min(1).max(MAX_CARRIES).optional(),
    grant: rewardGrantSchema.optional(),
    cardEditing: memberCardPartsSchema.optional(),
    personalRole: personalRolePartsSchema.optional(),
    levelUpImageId: z.uuid().optional(),
  })
  .refine(
    (reward) =>
      reward.grant !== undefined ||
      isCardReward(reward) ||
      reward.levelUpImageId !== undefined ||
      reward.personalRole !== undefined ||
      reward.carries !== undefined,
    {
      message:
        "a reward hands out a role, card editing, a level-up card or a personal role, or carries a perk",
      path: ["grant"],
    },
  )
  .refine((reward) => !carriesOf(reward).includes(reward.id), {
    message: "a perk cannot carry itself",
    path: ["carries"],
  })
  .refine((reward) => unique(carriesOf(reward)), {
    message: "a perk carries another once",
    path: ["carries"],
  })
  .refine((reward) => reward.cardEditing === undefined || reward.cardEditing.length > 0, {
    message: "a reward handing out card editing names a part",
    path: ["cardEditing"],
  })
  .refine((reward) => appsFitMetric(reward.trigger), {
    message: "only time in apps can be narrowed to some apps",
    path: ["trigger"],
  })
  .refine(
    (reward) =>
      reward.grant?.kind !== "access" ||
      (reward.trigger.kind === "role" && reward.requires === undefined),
    {
      message:
        "access lands on a role held, so only a perk for holding one, and nothing else, gives it",
      path: ["grant"],
    },
  );

export type Reward = z.infer<typeof rewardSchema>;

export const configuredRoleOf = (grant: RewardGrant | undefined): string | null => {
  if (grant === undefined) return null;
  switch (grant.kind) {
    case "role":
      return grant.roleId;
    case "managed_role":
      return grant.role.roleId;
    case "access":
      return null;
  }
};

export const cardPartsOf = (reward: CardEditing): readonly MemberCardPart[] =>
  reward.cardEditing === undefined ? [] : reward.cardEditing;

export const isCardReward = (reward: CardEditing): boolean => cardPartsOf(reward).length > 0;

const roleAnchorsSchema = z.strictObject({
  perks: snowflake.nullable(),
  personal: snowflake.nullable(),
});

export const ROLE_ANCHORS = roleAnchorsSchema.keyof().options;

export type RoleAnchor = (typeof ROLE_ANCHORS)[number];

export type RoleAnchors = z.infer<typeof roleAnchorsSchema>;

const rewardsSchema = z
  .strictObject({
    stacking: z.enum(REWARD_STACKING),
    anchors: roleAnchorsSchema,
    restoreOnRejoin: z.boolean(),
    drift: z.enum(DRIFT_POLICIES),
    items: z.array(rewardSchema).max(MAX_REWARDS),
  })
  .refine((rewards) => unique(rewards.items.map((item) => item.id)), {
    message: "each reward needs its own id",
  })
  .refine(
    (rewards) =>
      unique(
        rewards.items.flatMap((item) => {
          const roleId = configuredRoleOf(item.grant);
          return roleId === null ? [] : [roleId];
        }),
      ),
    { message: "a role can be handed out by one reward only" },
  )
  .refine(
    (rewards) => {
      const handed = new Set(
        rewards.items.flatMap((item) => {
          const roleId = configuredRoleOf(item.grant);
          return roleId === null ? [] : [roleId];
        }),
      );
      return rewards.items.every((item) =>
        readRolesOf(item).every((roleId) => !handed.has(roleId)),
      );
    },
    { message: "a perk cannot be given for holding a role a perk hands out", path: ["items"] },
  )
  .refine(
    (rewards) => {
      const ids = new Set(rewards.items.map((item) => item.id));
      return rewards.items.every((item) => carriesOf(item).every((id) => ids.has(id)));
    },
    { message: "a perk carries only perks that exist", path: ["items"] },
  )
  .refine(
    (rewards) => rewards.items.every((item) => item.carries === undefined || mayCarry(item)),
    {
      message:
        "a perk carries others onto a role: the role its holders hold, with no further condition, or the role Colossus runs for it",
      path: ["items"],
    },
  );

export type Rewards = z.infer<typeof rewardsSchema>;

export const CARD_LAYER_TYPES = ["image/png", "image/webp"] as const;

export const CARD_LAYER_MAX_EDGE = 1024;

export const CARD_WIDTH = 640;

export const CARD_BACKGROUND_MAX_WIDTH = 2400;

export const CARD_BACKGROUND_MAX_HEIGHT = 1600;

export const CARD_UPLOAD_MAX_BYTES = 2 * 1024 * 1024;

export const MAX_XP_ADJUSTMENT = MAX_XP;

const IMPORT_CONVERSIONS = ["level", "xp"] as const;

export type ImportConversion = (typeof IMPORT_CONVERSIONS)[number];

const importSettingSchema = z.strictObject({
  counts: z.boolean(),
  conversion: z.enum(IMPORT_CONVERSIONS),
  weight: z.number().int().min(1).max(100),
});

export type ImportSetting = z.infer<typeof importSettingSchema>;

export const DEFAULT_IMPORT_SETTING: ImportSetting = {
  counts: true,
  conversion: "level",
  weight: 100,
};

const importSettingsSchema = z.record(z.uuid(), importSettingSchema);

export type ImportSettings = Readonly<Record<string, ImportSetting>>;

export const CLAIM_MODES = ["off", "review"] as const;

export type ClaimMode = (typeof CLAIM_MODES)[number];

export const levelingConfigSchema = z.strictObject({
  formula: formulaSchema,
  announce: announceSchema,
  rewards: rewardsSchema,
  commands: z.strictObject({ leaderboard: z.enum(COMMAND_VISIBILITIES) }),
  card: z.strictObject({
    design: cardDesignSchema.nullable(),
    image: cardChoiceSchema.optional(),
    reply: messageTemplateSchema.nullable().optional(),
    members: memberCardsSchema.optional(),
  }),
  imports: importSettingsSchema.optional(),
  claims: z.enum(CLAIM_MODES).optional(),
});

export type LevelingConfig = z.infer<typeof levelingConfigSchema>;

export const rankReplyOf = (config: Pick<LevelingConfig, "card">): MessageTemplate | null =>
  config.card.reply === undefined ? null : config.card.reply;

export const claimModeOf = (config: Pick<LevelingConfig, "claims">): ClaimMode =>
  config.claims === undefined ? "off" : config.claims;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const without = (record: Record<string, unknown>, key: string): Record<string, unknown> =>
  Object.fromEntries(Object.entries(record).filter(([name]) => name !== key));

const handsOutNoRole = (item: unknown): boolean =>
  isRecord(item) && isRecord(item.grant) && item.grant.kind === "none";

const asRewardRead = (item: unknown): unknown => {
  if (!isRecord(item)) return item;
  const read = without(item, "perks");
  return isRecord(read.grant) && read.grant.kind === "card"
    ? { ...without(read, "grant"), cardEditing: true }
    : read;
};

const asPartsRead = (
  card: unknown,
  items: readonly unknown[],
): { card: unknown; items: unknown[] } => {
  const members = isRecord(card) && isRecord(card.members) ? card.members : null;
  const legacy = items.some((item) => isRecord(item) && typeof item.cardEditing === "boolean");
  if (!legacy && (members === null || !("who" in members))) return { card, items: [...items] };
  const who = members !== null && isRecord(members.who) ? members.who.kind : null;
  const parts: unknown = members === null ? [] : members.allow;
  const byReward = who === "reward" ? parts : [];
  return {
    card:
      members === null || !isRecord(card)
        ? card
        : { ...card, members: { allow: who === "reward" ? [] : parts } },
    items: items.flatMap((item) => {
      if (!isRecord(item) || typeof item.cardEditing !== "boolean") return [item];
      const rest = without(item, "cardEditing");
      const handed = item.cardEditing && Array.isArray(byReward) && byReward.length > 0;
      if (handed) return [{ ...rest, cardEditing: byReward }];
      return rest.grant === undefined && rest.levelUpImageId === undefined ? [] : [rest];
    }),
  };
};

const heldBy = (item: unknown, roleId: string): item is Record<string, unknown> =>
  isRecord(item) &&
  isRecord(item.trigger) &&
  item.trigger.kind === "role" &&
  item.trigger.roleId === roleId;

const carrierOf = (items: unknown[], roleId: string, carried: unknown): unknown => {
  let at = items.findIndex((item) => heldBy(item, roleId) && item.requires === undefined);
  if (at < 0) {
    items.push({ id: `carrier-${roleId}`, trigger: { kind: "role", roleId } });
    at = items.length - 1;
  }
  const carrier = items[at];
  if (!isRecord(carrier)) return null;
  const carries: unknown[] = Array.isArray(carrier.carries) ? carrier.carries : [];
  items[at] = { ...carrier, carries: carries.includes(carried) ? carries : [...carries, carried] };
  return carrier.id;
};

const asTiersRead = (tiers: unknown, items: unknown[]): void => {
  if (!Array.isArray(tiers)) return;
  let below: unknown = null;
  for (const roleId of tiers) {
    if (typeof roleId !== "string") continue;
    if (below === null) {
      const lowest = items.find((item) => heldBy(item, roleId));
      below = lowest === undefined ? null : lowest.id;
      continue;
    }
    below = carrierOf(items, roleId, below);
  }
};

const isStep = (item: Record<string, unknown>): boolean =>
  isRecord(item.trigger) && ON_LADDER_KINDS.includes(item.trigger.kind);

const asPassesRead = (items: unknown[]): void => {
  const passing = items.flatMap((item, at) =>
    isRecord(item) && Array.isArray(item.also) ? [{ at, item, passes: item.also }] : [],
  );
  for (const { at, item } of passing) items[at] = without(item, "also");
  for (const { item, passes } of passing) {
    if (!isStep(item)) continue;
    for (const pass of passes) {
      if (isRecord(pass) && pass.kind === "role" && typeof pass.roleId === "string") {
        carrierOf(items, pass.roleId, item.id);
      }
    }
  }
};

const carriesOntoRole = (item: Record<string, unknown>): boolean =>
  (isRecord(item.trigger) && item.trigger.kind === "role" && item.requires === undefined) ||
  (isRecord(item.grant) && item.grant.kind === "managed_role");

const OWN_THEN = ["grant", "cardEditing", "levelUpImageId", "personalRole"] as const;

const asCarriersRead = (items: readonly unknown[]): unknown[] => {
  const ids = new Set(items.flatMap((item) => (isRecord(item) ? [item.id] : [])));
  const next = items.flatMap((item) => {
    if (!isRecord(item) || !Array.isArray(item.carries)) return [item];
    const carries = carriesOntoRole(item) ? item.carries.filter((id) => ids.has(id)) : [];
    if (carries.length === item.carries.length) return [item];
    const rest = carries.length === 0 ? without(item, "carries") : { ...item, carries };
    return carries.length > 0 || OWN_THEN.some((key) => key in rest) ? [rest] : [];
  });
  return next.length === items.length && next.every((item, at) => item === items[at])
    ? next
    : asCarriersRead(next);
};

const stepOf = (item: unknown): readonly [number, number] | null => {
  if (!isRecord(item) || !isRecord(item.trigger) || !isRecord(item.grant)) return null;
  const { trigger } = item;
  if (trigger.kind !== "level" || typeof trigger.level !== "number") return null;
  if (item.grant.kind !== "managed_role") return null;
  return [typeof trigger.prestige === "number" ? trigger.prestige : 0, trigger.level];
};

const stepBelow = (left: readonly [number, number], right: readonly [number, number]): boolean =>
  left[0] < right[0] || (left[0] === right[0] && left[1] < right[1]);

const asInheritRead = (items: unknown[]): void => {
  const steps = items.flatMap((item, at) => {
    const step = stepOf(item);
    return step === null || !isRecord(item) ? [] : [{ at, step, id: item.id }];
  });
  for (const { at, step } of steps) {
    const lower = steps.filter((other) => stepBelow(other.step, step));
    const nearest = lower.reduce<readonly [number, number] | null>(
      (best, other) => (best === null || stepBelow(best, other.step) ? other.step : best),
      null,
    );
    if (nearest === null) continue;
    for (const other of lower) {
      if (stepBelow(other.step, nearest)) continue;
      const item = items[at];
      if (!isRecord(item)) continue;
      const carries: unknown[] = Array.isArray(item.carries) ? item.carries : [];
      if (!carries.includes(other.id)) items[at] = { ...item, carries: [...carries, other.id] };
    }
  }
};

const asCarriesRead = (rewards: Record<string, unknown>): Record<string, unknown> => {
  const rest = without(rewards, "roleTiers");
  if (!Array.isArray(rest.items)) return rest;
  const items: unknown[] = [...rest.items];
  const inherits = rewards.stacking === "highest_inherit";
  asTiersRead(rewards.roleTiers, items);
  if (inherits) asInheritRead(items);
  asPassesRead(items);
  return { ...rest, ...(inherits ? { stacking: "highest" } : {}), items: asCarriersRead(items) };
};

const asCurrentlyRead = (stored: unknown): unknown => {
  if (!isRecord(stored)) return stored;
  const { claims } = stored;
  const rewards = isRecord(stored.rewards) ? asCarriesRead(stored.rewards) : stored.rewards;
  const items: unknown = isRecord(rewards) ? rewards.items : undefined;
  const read = asPartsRead(
    stored.card,
    Array.isArray(items)
      ? items.filter((item: unknown) => !handsOutNoRole(item)).map(asRewardRead)
      : [],
  );
  const card = read.card;
  return {
    ...stored,
    ...(isRecord(rewards) && Array.isArray(items)
      ? { rewards: { ...rewards, items: read.items } }
      : {}),
    ...(isRecord(card)
      ? {
          card: {
            ...without(card, "approveUploads"),
            ...("design" in card ? { design: currentCardDesign(card.design) } : {}),
          },
        }
      : {}),
    ...(claims === "auto" ? { claims: "review" } : {}),
  };
};

const EVERYONE_CARD_PERK = "everyone-card";

export const withEveryoneCardPerk = (config: LevelingConfig, guildId: string): LevelingConfig => {
  const allow = config.card.members === undefined ? [] : config.card.members.allow;
  if (allow.length === 0) return config;
  const taken = new Set(config.rewards.items.map((item) => item.id));
  let id = EVERYONE_CARD_PERK;
  for (let n = 2; taken.has(id); n += 1) id = `${EVERYONE_CARD_PERK}-${n}`;
  return {
    ...config,
    card: { ...config.card, members: { allow: [] } },
    rewards: {
      ...config.rewards,
      items: [
        ...config.rewards.items,
        { id, trigger: { kind: "role", roleId: guildId }, cardEditing: [...allow] },
      ],
    },
  };
};

export const storedLevelingConfigSchema = z.preprocess(asCurrentlyRead, levelingConfigSchema);

const createdRoleNames = (config: LevelingConfig): readonly [number, string][] =>
  config.rewards.items.flatMap((item, index): [number, string][] =>
    item.grant?.kind === "managed_role" && item.grant.role.appearance !== null
      ? [[index, item.grant.role.appearance.name]]
      : [],
  );

const templateIssues = (
  template: MessageTemplate | null,
  at: readonly string[],
  context: z.RefinementCtx,
): void => {
  if (template === null) return;
  if (!templateShowsText(template)) {
    context.addIssue({
      code: "custom",
      path: [...at],
      message: "empty message",
      params: { reason: "empty_message" satisfies TemplateRefusal },
    });
  }
  const overLimit = pictureOverLimit(template);
  if (overLimit !== null) {
    context.addIssue({
      code: "custom",
      path: [...at, ...overLimit],
      message: "too many pictures",
      params: { reason: "too_many_pictures" satisfies TemplateRefusal },
    });
  }
  for (const path of reorderingTexts(template)) {
    context.addIssue({ code: "custom", path: [...at, ...path], message: TEXT_REORDERS });
  }
};

export const savedLevelingConfigSchema = levelingConfigSchema.superRefine((config, context) => {
  if (config.card.members !== undefined && config.card.members.allow.length > 0) {
    context.addIssue({
      code: "custom",
      path: ["card", "members", "allow"],
      message:
        "card parts go to members through perks; a perk given for holding @everyone gives them to every member",
    });
  }
  const { curve, maxLevel } = config.formula;
  const highest = maxLevel === null ? MAX_LEVEL : maxLevel;
  const totals = curve.kind === "expression" ? curveTotals(curve.total, highest) : null;
  if (totals !== null && totals.length - 1 < highest) {
    const reached = totals.length - 1;
    context.addIssue({
      code: "custom",
      path: ["formula", "curve", "total"],
      params: { reason: "curve_ceiling" satisfies FormulaRefusal },
      message: `the formula passes the most XP that can be counted after level ${reached}, short of ${maxLevel === null ? `level ${MAX_LEVEL}; set a highest level it reaches` : `the highest level, ${maxLevel}`}`,
    });
  }
  templateIssues(config.announce.template, ["announce", "template"], context);
  templateIssues(rankReplyOf(config), ["card", "reply"], context);
  if (config.card.design !== null && config.card.image !== undefined) {
    context.addIssue({
      code: "custom",
      path: ["card", "image"],
      message: "the rank card is a design or a chosen card, not both",
    });
  }
  for (const [index, name] of createdRoleNames(config)) {
    const problem = nameProblem(name);
    if (problem !== null) {
      context.addIssue({
        code: "custom",
        path: ["rewards", "items", index, "grant", "role", "appearance", "name"],
        message: problem,
      });
    }
  }
});

const NO_SCOPE: ChannelScope = { channels: [], categories: [] };

export const DEFAULT_CURVE_TOTAL = "10L² + 15L";

export const DEFAULT_LEVELING_FORMULA: Formula = {
  terms: [
    { metric: "active_minutes", weight: 6 },
    {
      metric: "voice_minutes",
      weight: 1,
      voiceStates: ["normal", "self_mute", "server_mute"],
      countAlone: false,
    },
  ],
  filters: { exclude: NO_SCOPE, excludeRoles: [], onlyRoles: [] },
  multipliers: [],
  curve: { kind: "expression", total: DEFAULT_CURVE_TOTAL },
  maxLevel: null,
};

export const DEFAULT_LEVELING_CONFIG: LevelingConfig = {
  formula: DEFAULT_LEVELING_FORMULA,
  announce: { mode: "same_channel", channelId: null, template: null, when: { kind: "reward" } },
  rewards: {
    stacking: "highest",
    anchors: { perks: null, personal: null },
    restoreOnRejoin: true,
    drift: "flag",
    items: [],
  },
  commands: { leaderboard: "ephemeral" },
  card: { design: null },
};

const LEVELING_ADVANCED_FEATURES = ["formula", "rewards", "template", "card", "reply"] as const;

export type LevelingAdvancedFeature = (typeof LEVELING_ADVANCED_FEATURES)[number];

const canonical = (value: unknown): string =>
  JSON.stringify(value, (_key, inner: unknown) =>
    inner !== null && typeof inner === "object" && !Array.isArray(inner)
      ? Object.fromEntries(
          Object.entries(inner as Record<string, unknown>).sort(([a], [b]) => a.localeCompare(b)),
        )
      : inner,
  );

const sameFormula = (left: Formula, right: Formula): boolean =>
  canonical(left) === canonical(right);

const unpausedTerm = ({ paused: _paused, ...term }: FormulaTerm): FormulaTerm => term;

const earning = (formula: Formula): unknown => ({
  ...formula,
  curve: null,
  maxLevel: null,
  prestige: null,
});

export const sameXpFormula = (left: Formula, right: Formula): boolean =>
  canonical(earning(left)) === canonical(earning(right));

export const EARNING_LIMITS = ["channels", "roles-except", "roles-only"] as const;

export type EarningLimit = (typeof EARNING_LIMITS)[number];

export const EARNING_NAME_MAX = 100;

export const MAX_EARNING_NAMES = LEVELING_METRICS.length + EARNING_LIMITS.length + 1;

const earningLimitSchema = z.strictObject({
  on: z.enum(EARNING_LIMITS),
  ids: z.array(snowflake).max(DISCORD_MAX_CHANNELS),
});

export const earningRulesSchema = z.strictObject({
  terms: z.array(formulaTermSchema),
  multipliers: z.array(multiplierSchema),
  limits: z.array(earningLimitSchema),
});

export type EarningRules = z.infer<typeof earningRulesSchema>;

export const earningNamesSchema = z
  .array(z.string().trim().min(1).max(EARNING_NAME_MAX))
  .min(1)
  .max(MAX_EARNING_NAMES);

export const earningMarkSchema = earningRulesSchema.extend({ names: earningNamesSchema });

export type EarningMark = z.infer<typeof earningMarkSchema>;

export interface EarningEdit extends EarningRules {
  readonly dropped: Pick<EarningRules, "terms" | "multipliers">;
}

const limitIdsIn = (filters: FormulaFilters, on: EarningLimit): string[] =>
  (on === "channels"
    ? [...filters.exclude.categories, ...filters.exclude.channels]
    : on === "roles-only"
      ? filters.onlyRoles
      : filters.excludeRoles
  ).toSorted();

const missingFrom = <T>(list: readonly T[], from: readonly T[]): T[] => {
  const held = new Set(from.map(canonical));
  return list.filter((item) => !held.has(canonical(item)));
};

export const earningEdit = (before: Formula, after: Formula): EarningEdit | null => {
  const edit: EarningEdit = {
    terms: missingFrom(after.terms, before.terms),
    multipliers: missingFrom(after.multipliers, before.multipliers),
    limits: EARNING_LIMITS.flatMap((on) => {
      const ids = limitIdsIn(after.filters, on);
      return canonical(ids) === canonical(limitIdsIn(before.filters, on)) ? [] : [{ on, ids }];
    }),
    dropped: {
      terms: missingFrom(before.terms, after.terms),
      multipliers: missingFrom(before.multipliers, after.multipliers),
    },
  };
  const changes =
    edit.terms.length +
    edit.multipliers.length +
    edit.limits.length +
    edit.dropped.terms.length +
    edit.dropped.multipliers.length;
  return changes === 0 ? null : edit;
};

export const mergedEarningMark = (
  held: EarningMark | null,
  edit: EarningEdit,
  names: readonly string[],
): EarningMark => {
  const base = held === null ? { names: [], terms: [], multipliers: [], limits: [] } : held;
  const union = <T>(kept: readonly T[], added: readonly T[]): T[] => [
    ...kept,
    ...missingFrom(added, kept),
  ];
  return {
    names: union(base.names, names).slice(0, MAX_EARNING_NAMES),
    terms: union(base.terms, edit.terms),
    multipliers: union(base.multipliers, edit.multipliers),
    limits: [
      ...base.limits.filter((limit) => !edit.limits.some((next) => next.on === limit.on)),
      ...edit.limits,
    ],
  };
};

export type EarningRule =
  | { readonly kind: "term"; readonly term: FormulaTerm }
  | { readonly kind: "multiplier"; readonly multiplier: Multiplier }
  | { readonly kind: "limit"; readonly on: EarningLimit; readonly ids: readonly string[] };

interface HeldEarningRules {
  readonly terms: readonly unknown[];
  readonly multipliers: readonly unknown[];
  readonly limits: readonly { readonly on: EarningLimit; readonly ids: readonly string[] }[];
}

export const earningRulesHold = (rules: HeldEarningRules, rule: EarningRule): boolean => {
  switch (rule.kind) {
    case "term":
      return rules.terms.some((term) => canonical(term) === canonical(rule.term));
    case "multiplier":
      return rules.multipliers.some(
        (multiplier) => canonical(multiplier) === canonical(rule.multiplier),
      );
    case "limit": {
      const ids = canonical(rule.ids.toSorted());
      return rules.limits.some((limit) => limit.on === rule.on && canonical(limit.ids) === ids);
    }
  }
};

export const sameLevelReading = (left: Formula, right: Formula): boolean =>
  canonical({ curve: left.curve, maxLevel: left.maxLevel, prestige: left.prestige ?? null }) ===
  canonical({ curve: right.curve, maxLevel: right.maxLevel, prestige: right.prestige ?? null });

const importSettingOf = (settings: ImportSettings | undefined, runId: string): ImportSetting => {
  const found = settings === undefined ? undefined : settings[runId];
  return found === undefined ? DEFAULT_IMPORT_SETTING : found;
};

const importSettingsInEffect = (settings: ImportSettings | undefined): ImportSettings =>
  Object.fromEntries(
    Object.entries(settings === undefined ? {} : settings).filter(
      ([, setting]) => canonical(setting) !== canonical(DEFAULT_IMPORT_SETTING),
    ),
  );

export const withImportsInEffect = (config: LevelingConfig): LevelingConfig => {
  const { imports, ...rest } = config;
  const inEffect = importSettingsInEffect(imports);
  return Object.keys(inEffect).length === 0 ? rest : { ...rest, imports: inEffect };
};

export const sameImportSettings = (
  left: ImportSettings | undefined,
  right: ImportSettings | undefined,
): boolean => canonical(importSettingsInEffect(left)) === canonical(importSettingsInEffect(right));

export const isCoreFormula = (formula: Formula): boolean => {
  const sources = { ...formula, multipliers: [], terms: formula.terms.map(unpausedTerm) };
  return (
    sameFormula(sources, DEFAULT_LEVELING_FORMULA) ||
    CORE_SWITCH_FORMULAS().some((core) => sameFormula(sources, core))
  );
};

const isCoreTrigger = (trigger: RewardTrigger): boolean =>
  trigger.kind === "level" ? trigger.prestige === undefined : trigger.kind !== "prestige";

export const isCoreReward = (reward: Reward): boolean =>
  isCoreTrigger(reward.trigger) &&
  reward.requires === undefined &&
  reward.personalRole === undefined &&
  (reward.grant?.kind === "role" || reward.grant?.kind === "managed_role");

const isCoreCard = (design: CardDesign | null): boolean => design === null || isBuiltInCard(design);

type LibraryImage = Extract<CardChoice, { readonly kind: "image" }>;

const libraryImage = (choice: CardChoice | undefined): LibraryImage | null =>
  choice !== undefined && choice.kind === "image" ? choice : null;

const handsOutAdvanced = (reward: Reward): boolean =>
  reward.grant !== undefined || reward.personalRole !== undefined;

const rolePart = (reward: Reward): unknown =>
  !handsOutAdvanced(reward)
    ? null
    : {
        id: reward.id,
        trigger: reward.trigger,
        requires: reward.requires,
        grant: reward.grant,
        personalRole: reward.personalRole,
      };

const advancedPart = (config: LevelingConfig, feature: LevelingAdvancedFeature): unknown => {
  switch (feature) {
    case "formula":
      return isCoreFormula(config.formula) ? null : config.formula;
    case "rewards": {
      const advanced = config.rewards.items
        .filter((item) => handsOutAdvanced(item) && !isCoreReward(item))
        .map(rolePart);
      return advanced.length === 0 ? null : advanced;
    }
    case "template": {
      const { template, card } = config.announce;
      return template === null && card === undefined ? null : { template, card };
    }
    case "card": {
      const image = libraryImage(config.card.image);
      const design = isCoreCard(config.card.design) ? null : config.card.design;
      return image === null && design === null ? null : { image, design };
    }
    case "reply":
      return rankReplyOf(config);
  }
};

export const advancedFeaturesUsed = (config: LevelingConfig): readonly LevelingAdvancedFeature[] =>
  LEVELING_ADVANCED_FEATURES.filter((feature) => advancedPart(config, feature) !== null);

export const advancedFeaturesChanged = (
  stored: LevelingConfig,
  next: LevelingConfig,
): readonly LevelingAdvancedFeature[] =>
  LEVELING_ADVANCED_FEATURES.filter(
    (feature) =>
      canonical(advancedPart(stored, feature)) !== canonical(advancedPart(next, feature)),
  );

const APPLIED_VIEWS = ["full", "core"] as const;

export type AppliedView = (typeof APPLIED_VIEWS)[number];

const appliedViewOf = (advanced: boolean): AppliedView => (advanced ? "full" : "core");

export const appliedScopeRead = (stored: string | null): AppliedView | undefined =>
  stored === null ? "full" : APPLIED_VIEWS.find((known) => known === stored);

export interface LevelingView {
  readonly scope: AppliedView;
  readonly rules: number | null;
  readonly since?: Date | null;
}

export const levelingViewOf = (advanced: boolean, rules: number | null): LevelingView => ({
  scope: appliedViewOf(advanced),
  rules,
});

export const sameView = (a: LevelingView, b: LevelingView): boolean =>
  a.scope === b.scope && a.rules === b.rules;

export const earningRules = (
  formula: Formula,
  rules: number | null,
  asOf: Date | null = null,
): Formula => {
  if (rules === null) return formula;
  let left = rules;
  const within = <T extends { readonly paused?: true | undefined }>(rule: T): T => {
    if (rule.paused === true) return rule;
    if (left > 0) {
      left -= 1;
      return rule;
    }
    return { ...rule, paused: true };
  };
  return {
    ...formula,
    terms: formula.terms.map(within),
    multipliers: formula.multipliers.map((rule) =>
      asOf !== null && multiplierEnded(rule, asOf) ? rule : within(rule),
    ),
  };
};

const coreRewardPart = (reward: Reward): Reward | null => {
  const role = reward.grant !== undefined && isCoreReward(reward) ? reward.grant : undefined;
  const editing = reward.cardEditing;
  const carries =
    isCoreTrigger(reward.trigger) && reward.requires === undefined ? reward.carries : undefined;
  if (role === undefined && editing === undefined && carries === undefined) return null;
  return {
    id: reward.id,
    trigger: reward.trigger,
    ...(role === undefined ? {} : { grant: role }),
    ...(editing === undefined ? {} : { cardEditing: editing }),
    ...(carries === undefined ? {} : { carries }),
  };
};

const coreScoped = (config: LevelingConfig, advanced: boolean): LevelingConfig => {
  if (advanced) return config;
  const { card: _levelUpCard, ...announce } = config.announce;
  const image = config.card.image;
  return {
    ...config,
    formula: isCoreFormula(config.formula) ? config.formula : DEFAULT_LEVELING_FORMULA,
    announce: { ...announce, template: null },
    rewards: {
      ...config.rewards,
      items: config.rewards.items.flatMap((item) => {
        const kept = coreRewardPart(item);
        return kept === null ? [] : [kept];
      }),
    },
    card: {
      design: isCoreCard(config.card.design) ? config.card.design : null,
      ...(image !== undefined && image.kind === "look" ? { image } : {}),
      ...(config.card.members === undefined ? {} : { members: config.card.members }),
    },
  };
};

const perkRolesOf = (reward: Reward): readonly string[] => {
  const handed = configuredRoleOf(reward.grant);
  return [
    ...(reward.trigger.kind === "role" ? [reward.trigger.roleId] : []),
    ...(handed === null ? [] : [handed]),
  ];
};

const carrierRolesOf = (reward: Reward): readonly string[] => [
  ...(reward.trigger.kind === "role" && carriesAccessFrom(reward) ? [reward.trigger.roleId] : []),
  ...(reward.grant?.kind === "managed_role" && reward.grant.role.roleId !== null
    ? [reward.grant.role.roleId]
    : []),
];

export const withCarriedMultipliers = (config: LevelingConfig): LevelingConfig => {
  const closures = carriedClosures(config.rewards.items);
  const extra = new Map<string, Set<string>>();
  for (const carrier of config.rewards.items) {
    const into = carrierRolesOf(carrier);
    if (into.length === 0) continue;
    for (const carried of closures.get(carrier.id) ?? []) {
      for (const roleId of perkRolesOf(carried)) {
        extra.set(roleId, new Set([...(extra.get(roleId) ?? []), ...into]));
      }
    }
  }
  if (extra.size === 0) return config;
  let widened = false;
  const multipliers = config.formula.multipliers.map((rule) => {
    const added = [
      ...new Set(rule.roles.flatMap((roleId) => [...(extra.get(roleId) ?? [])])),
    ].filter((roleId) => !rule.roles.includes(roleId));
    if (added.length === 0) return rule;
    widened = true;
    return { ...rule, roles: [...rule.roles, ...added] };
  });
  return widened ? { ...config, formula: { ...config.formula, multipliers } } : config;
};

export const effectiveConfig = (
  config: LevelingConfig,
  advanced: boolean,
  rules: number | null = null,
  asOf: Date | null = null,
): LevelingConfig => {
  const scoped = coreScoped(config, advanced);
  const formula = earningRules(scoped.formula, rules, asOf);
  return withCarriedMultipliers(formula === scoped.formula ? scoped : { ...scoped, formula });
};

const MEMBER_CARD_FEATURES = ["members", "rewards"] as const;

export type MemberCardFeature = (typeof MEMBER_CARD_FEATURES)[number];

const memberCardPart = (config: LevelingConfig, feature: MemberCardFeature): unknown => {
  switch (feature) {
    case "members":
      return config.card.members === undefined ? null : config.card.members;
    case "rewards": {
      const card = config.rewards.items
        .filter(isCardReward)
        .map((item) => ({ id: item.id, trigger: item.trigger, parts: item.cardEditing }));
      return card.length === 0 ? null : card;
    }
  }
};

export const memberCardFeaturesChanged = (
  stored: LevelingConfig,
  next: LevelingConfig,
): readonly MemberCardFeature[] =>
  MEMBER_CARD_FEATURES.filter(
    (feature) =>
      canonical(memberCardPart(stored, feature)) !== canonical(memberCardPart(next, feature)),
  );

export const withMemberCardsInEffect = (
  config: LevelingConfig,
  granted: boolean,
): LevelingConfig => {
  if (granted) return config;
  const { members: _members, ...card } = config.card;
  return {
    ...config,
    card,
    rewards: {
      ...config.rewards,
      items: config.rewards.items.flatMap((item) => {
        if (!isCardReward(item)) return [item];
        const { cardEditing: _editing, ...rest } = item;
        return rest.grant === undefined && rest.levelUpImageId === undefined ? [] : [rest];
      }),
    },
  };
};

const rewardCardsPart = (config: LevelingConfig): unknown =>
  config.rewards.items.flatMap((item) =>
    item.levelUpImageId === undefined
      ? []
      : [{ id: item.id, trigger: item.trigger, levelUpImageId: item.levelUpImageId }],
  );

export const rewardCardsChanged = (stored: LevelingConfig, next: LevelingConfig): boolean =>
  canonical(rewardCardsPart(stored)) !== canonical(rewardCardsPart(next));

export const withRewardCardsInEffect = (
  config: LevelingConfig,
  granted: boolean,
): LevelingConfig => {
  if (granted) return config;
  return {
    ...config,
    rewards: {
      ...config.rewards,
      items: config.rewards.items.flatMap((item) => {
        const { levelUpImageId: _image, ...rest } = item;
        return rest.grant === undefined && !isCardReward(rest) ? [] : [rest];
      }),
    },
  };
};

const namedImages = (config: LevelingConfig): readonly (readonly [string, ImageUse])[] => {
  const rank = libraryImage(config.card.image);
  const levelUp = libraryImage(config.announce.card);
  return [
    ...(rank === null ? [] : [[rank.imageId, { kind: "rank_card" }] as const]),
    ...(levelUp === null ? [] : [[levelUp.imageId, { kind: "level_up_card" }] as const]),
    ...config.rewards.items.flatMap((item) =>
      item.levelUpImageId === undefined
        ? []
        : [[item.levelUpImageId, { kind: "reward", rewardId: item.id }] as const],
    ),
  ];
};

export const imagesNamed = (config: LevelingConfig): readonly string[] => [
  ...new Set(namedImages(config).map(([imageId]) => imageId)),
];

export const imageUsesOf = (config: LevelingConfig, imageId: string): readonly ImageUse[] =>
  namedImages(config).flatMap(([named, use]) => (named === imageId ? [use] : []));

export interface LevelUpCard {
  readonly card: CardChoice;
  readonly rewardId?: string;
}

export const levelUpCardOf = (
  config: LevelingConfig,
  reached: readonly Reward[],
): LevelUpCard | null => {
  const highest = reached.at(-1);
  const named = highest === undefined ? {} : { rewardId: highest.id };
  const own = reached.findLast((reward) => reward.levelUpImageId !== undefined);
  if (own !== undefined && own.levelUpImageId !== undefined) {
    return { card: { kind: "image", imageId: own.levelUpImageId }, ...named };
  }
  return config.announce.card === undefined ? null : { card: config.announce.card, ...named };
};

const checkpointXp = (
  levels: readonly number[],
  totals: readonly number[],
  level: number,
): number => {
  const at = levels.findIndex((checkpoint) => checkpoint >= level);
  if (at < 0) return Number.POSITIVE_INFINITY;
  const upper = levels[at]!;
  const upperTotal = totals[at]!;
  if (upper === level) return upperTotal;
  const lower = at === 0 ? 0 : levels[at - 1]!;
  const lowerTotal = at === 0 ? 0 : totals[at - 1]!;
  return Math.round(lowerTotal + ((upperTotal - lowerTotal) * (level - lower)) / (upper - lower));
};

const totalsOf = (curve: Curve): ((level: number) => number) => {
  if (curve.kind === "expression") {
    const totals = curveTotals(curve.total, MAX_LEVEL);
    return (level) => (level <= 0 ? 0 : (totals?.[level] ?? Number.POSITIVE_INFINITY));
  }
  const { levels, thresholds } = curve;
  if (levels !== undefined) {
    return (level) => (level <= 0 ? 0 : checkpointXp(levels, thresholds, level));
  }
  return (level) => (level <= 0 ? 0 : (thresholds[level - 1] ?? Number.POSITIVE_INFINITY));
};

export const xpForLevel = (curve: Curve, level: number): number =>
  level <= 0 ? 0 : totalsOf(curve)(level);

export const curveCeiling = (curve: Curve, maxLevel: number | null): number => {
  const cap = maxLevel ?? MAX_LEVEL;
  if (curve.kind === "table") return Math.min(cap, curve.levels?.at(-1) ?? curve.thresholds.length);
  const totals = curveTotals(curve.total, MAX_LEVEL);
  return Math.min(cap, totals === null ? 0 : totals.length - 1);
};

export const levelCeiling = (formula: Formula): number =>
  curveCeiling(
    formula.curve,
    formula.prestige === undefined || formula.prestige === null ? formula.maxLevel : null,
  );

export const xpCeiling = (formula: Formula): number =>
  xpForLevel(formula.curve, levelCeiling(formula));

export const levelForXp = (curve: Curve, xp: number, maxLevel: number | null = null): number =>
  !Number.isFinite(xp) || xp <= 0
    ? 0
    : highestAtMost(curveCeiling(curve, maxLevel), totalsOf(curve), xp);

export interface LevelProgress {
  readonly level: number;
  readonly xpIntoLevel: number;
  readonly xpForNext: number | null;
}

export const levelProgress = (curve: Curve, xp: number, maxLevel: number | null): LevelProgress => {
  const xpAt = totalsOf(curve);
  const level = levelForXp(curve, xp, maxLevel);
  const floor = xpAt(level);
  const atCeiling = level >= curveCeiling(curve, maxLevel);
  return {
    level,
    xpIntoLevel: Math.max(0, xp - floor),
    xpForNext: atCeiling ? null : xpAt(level + 1) - floor,
  };
};

export interface ImportedStanding {
  readonly runId: string;
  readonly level: number;
  readonly progress: number | null;
  readonly xp: number | null;
  readonly observedAt: number;
  readonly forward?: number;
}

const importWorth = (curve: Curve, entry: ImportedStanding, setting: ImportSetting): number => {
  const ceiling = curveCeiling(curve, null);
  const top = xpForLevel(curve, ceiling);
  let converted: number;
  if (setting.conversion === "xp" && entry.xp !== null) {
    converted = Math.min(entry.xp, top);
  } else {
    const level = Math.min(entry.level, ceiling);
    const floor = xpForLevel(curve, level);
    const into =
      entry.progress === null || level >= ceiling
        ? 0
        : Math.floor(
            Math.min(Math.max(entry.progress, 0), 1) * (xpForLevel(curve, level + 1) - floor),
          );
    converted = Math.min(floor + into, top);
  }
  return Math.floor((converted * setting.weight) / 100);
};

const standingRun = (
  curve: Curve,
  imports: readonly ImportedStanding[],
  settings: ImportSettings | undefined,
): { readonly worth: number; readonly forward: number } | null =>
  imports.reduce<{ readonly worth: number; readonly forward: number } | null>((best, entry) => {
    const setting = importSettingOf(settings, entry.runId);
    if (!setting.counts) return best;
    const worth = importWorth(curve, entry, setting);
    const forward = entry.forward === undefined ? 0 : Math.max(0, entry.forward);
    return best === null || worth + forward > best.worth + best.forward ? { worth, forward } : best;
  }, null);

export interface XpParts {
  readonly carried: number;
  readonly earned: number;
  readonly imports: readonly ImportedStanding[];
  readonly importSettings: ImportSettings | undefined;
  readonly adjustments: number;
}

const statisticsXp = (parts: Pick<XpParts, "carried" | "earned">): number =>
  parts.carried + parts.earned;

const standingXp = (curve: Curve, parts: XpParts): number => {
  const run = standingRun(curve, parts.imports, parts.importSettings);
  const statistics = statisticsXp(parts);
  return run === null ? statistics : Math.max(statistics, run.worth + run.forward);
};

export const totalXp = (curve: Curve, parts: XpParts): number =>
  Math.min(MAX_XP, Math.max(0, Math.floor(standingXp(curve, parts) + parts.adjustments)));

export const importedWorth = (
  curve: Curve,
  parts: Pick<XpParts, "imports" | "importSettings">,
): number => {
  const run = standingRun(curve, parts.imports, parts.importSettings);
  return run === null ? 0 : run.worth;
};

export interface LevelingState {
  readonly enabled: boolean;
  readonly config: LevelingConfig;
}

export const levelingLive = (config: Pick<LevelingConfig, "formula">): boolean =>
  config.formula.terms.some((term) => term.paused !== true);

const withTerms = (config: LevelingConfig, terms: readonly FormulaTerm[]): LevelingConfig => ({
  ...config,
  formula: { ...config.formula, terms: [...terms] },
});

export const pausedWhileOff = (state: LevelingState): LevelingConfig =>
  state.enabled
    ? state.config
    : withTerms(
        state.config,
        state.config.formula.terms.map((term) => ({ ...term, paused: true as const })),
      );

export const liveAfterSave = (before: LevelingState, config: LevelingConfig): boolean =>
  canonical(pausedWhileOff(before).formula.terms) === canonical(config.formula.terms)
    ? before.enabled
    : levelingLive(config);

export const storedWhileOff = (config: LevelingConfig, last: LevelingConfig): LevelingConfig => {
  if (levelingLive(config)) return config;
  const kept = last.formula.terms
    .filter((term) => term.paused !== true)
    .map((term) => canonical(unpausedTerm(term)));
  const terms = config.formula.terms.map((term) => {
    const at = kept.indexOf(canonical(unpausedTerm(term)));
    if (at < 0) return term;
    kept.splice(at, 1);
    return unpausedTerm(term);
  });
  return withTerms(
    config,
    terms.some((term) => term.paused !== true)
      ? terms
      : terms.map((term, index) => (index === 0 ? unpausedTerm(term) : term)),
  );
};

export interface RoleGrant {
  readonly key: string;
  readonly rewardId: string;
  readonly roleId: string | null;
  readonly created: boolean;
}

export const roleGrantsOf = (config: LevelingConfig): readonly RoleGrant[] =>
  config.rewards.items.flatMap((item): readonly RoleGrant[] => {
    if (item.grant?.kind === "role") {
      const roleId = item.grant.roleId;
      return [{ key: `role:${roleId}`, rewardId: item.id, roleId, created: false }];
    }
    if (item.grant?.kind === "managed_role") {
      const { roleId, mode } = item.grant.role;
      return mode === "adopt" && roleId !== null
        ? [{ key: `role:${roleId}`, rewardId: item.id, roleId, created: false }]
        : [{ key: `reward:${item.id}`, rewardId: item.id, roleId: null, created: true }];
    }
    return [];
  });

export const managedRoleGrants = (state: LevelingState): readonly RoleGrant[] =>
  state.enabled ? roleGrantsOf(state.config) : [];

export interface CreatedRoleRow {
  readonly rewardId: string;
  readonly roleId: string | null;
  readonly status: string;
}

export const createdRoleOf = (row: CreatedRoleRow | undefined): string | null =>
  row === undefined || row.status === "deleted" ? null : row.roleId;

export const createdRoles = (rows: Iterable<CreatedRoleRow>): ReadonlyMap<string, string> =>
  new Map(
    [...rows].flatMap((row) => {
      const roleId = createdRoleOf(row);
      return roleId === null ? [] : [[row.rewardId, roleId] as const];
    }),
  );

export const rewardRoleIdsOf = (
  config: LevelingConfig,
  created: ReadonlyMap<string, string>,
): readonly string[] => {
  const roleIds = roleGrantsOf(config).flatMap((grant) => {
    const roleId = grant.roleId ?? created.get(grant.rewardId);
    return roleId === undefined ? [] : [roleId];
  });
  return [...new Set(roleIds)].sort();
};

export const leavingRoleGrants = (
  before: LevelingState,
  after: LevelingState,
): readonly RoleGrant[] => {
  const staying = new Set(managedRoleGrants(after).map((grant) => grant.key));
  return managedRoleGrants(before)
    .filter((grant) => !staying.has(grant.key))
    .sort((left, right) => left.key.localeCompare(right.key));
};

export const LEVELING_BOTS: ReadonlyMap<string, string> = new Map([
  ["159985870458322944", "MEE6"],
  ["506186003816513538", "Lurkr"],
  ["518129184338345985", "Kiai"],
  ["339254240012664832", "AmariBot"],
  ["437808476106784770", "Arcane"],
  ["172002275412279296", "Tatsu"],
  ["338222603829510164", "Atlas"],
  ["170254782546575360", "Nadeko"],
  ["235148962103951360", "Carl-bot"],
  ["155149108183695360", "Dyno"],
  ["282859044593598464", "ProBot"],
  ["310848622642069504", "JuniperBot"],
]);

const MEE6_CURVE = "5L(2L² + 27L + 91)/6";

export const BOT_CURVES = {
  mee6: MEE6_CURVE,
  juniper: MEE6_CURVE,
  arcane: "50L² + 25L",
  atlas: "L³ + 104L",
  kiai: "12L² + 12L",
  nadeko: "4.5L² + 31.5L",
  lurkr: "50(L-1)² + 100",
  amari: "20(L-1)² + 35",
  polaris: "100·round((L³ + 50L² + 100L)/100)",
  dyno: "floor(35L^2.65/2.65)",
} as const;

export type BotCurve = keyof typeof BOT_CURVES;

export const importCurveSchema = z.discriminatedUnion("kind", [
  expressionCurveSchema,
  z.strictObject({
    kind: z.literal("per_level"),
    a: z.number().min(0).max(1_000_000),
    b: z.number().min(0).max(1_000_000),
    c: z.number().min(1).max(1_000_000),
  }),
  z
    .strictObject({
      kind: z.literal("cumulative"),
      coefficients: z.tuple([z.number().min(0), z.number().min(0), z.number().min(0)]),
      round: z.number().int().min(1).max(1_000),
    })
    .refine((curve) => cumulativeXp(curve.coefficients, curve.round, 1) >= 1, {
      message: "the curve must give level 1 at least 1 XP",
      path: ["coefficients"],
    }),
]);

export type ImportCurve = z.infer<typeof importCurveSchema>;

const botCurve = (name: BotCurve) => ({ kind: "expression", total: BOT_CURVES[name] }) as const;

export const IMPORT_CURVE_PRESETS = {
  mee6: botCurve("mee6"),
  lurkr: botCurve("lurkr"),
  kiai: botCurve("kiai"),
  nadeko: botCurve("nadeko"),
  atlas: botCurve("atlas"),
  amari: botCurve("amari"),
  dyno: botCurve("dyno"),
  arcane: botCurve("arcane"),
  polaris: botCurve("polaris"),
  polaris_generous: { kind: "cumulative", coefficients: [50, 20, 0.5], round: 50 },
} as const satisfies Record<string, ImportCurve>;

export type ImportCurvePreset = keyof typeof IMPORT_CURVE_PRESETS;

const MEE6_PACE = { metric: "active_minutes", weight: 15, weightMax: 25 } as const;

const BOT_PACES = {
  mee6: MEE6_PACE,
  juniper: MEE6_PACE,
  lurkr: { metric: "active_minutes", weight: 15, weightMax: 40 },
  atlas: { metric: "active_minutes", weight: 7, weightMax: 13 },
  polaris: { metric: "active_minutes", weight: 50, weightMax: 100 },
  amari: { metric: "messages", weight: 1 },
} as const satisfies Partial<Record<BotCurve, FormulaTerm>>;

export const switchFormula = (bot: BotCurve, pace: boolean): Formula => {
  const [, ...rest] = DEFAULT_LEVELING_FORMULA.terms;
  const known: FormulaTerm | undefined = (BOT_PACES as Partial<Record<BotCurve, FormulaTerm>>)[bot];
  return {
    ...DEFAULT_LEVELING_FORMULA,
    terms: pace && known !== undefined ? [{ ...known }, ...rest] : DEFAULT_LEVELING_FORMULA.terms,
    curve: { kind: "expression", total: BOT_CURVES[bot] },
  };
};

const CORE_SWITCH_FORMULAS = (): readonly Formula[] => {
  const bots = Object.keys(BOT_CURVES) as BotCurve[];
  const earnings = [
    DEFAULT_LEVELING_FORMULA.terms,
    ...bots.flatMap((bot) => (bot in BOT_PACES ? [switchFormula(bot, true).terms] : [])),
  ];
  const curves = [
    DEFAULT_LEVELING_FORMULA.curve,
    ...bots.map((bot) => switchFormula(bot, false).curve),
  ];
  return earnings.flatMap((terms) =>
    curves.map((curve) => ({ ...DEFAULT_LEVELING_FORMULA, terms, curve })),
  );
};

export const IMPORT_CURVE_PRESET_NAMES = Object.keys(
  IMPORT_CURVE_PRESETS,
) as readonly ImportCurvePreset[];

const cumulativeXp = (
  coefficients: readonly [number, number, number],
  round: number,
  level: number,
): number => {
  const [c1, c2, c3] = coefficients;
  const raw = c1 * level + c2 * level ** 2 + c3 * level ** 3;
  return round > 1 ? round * Math.round(raw / round) : Math.round(raw);
};

const perLevelXp = (curve: { a: number; b: number; c: number }, level: number): number => {
  const n = level - 1;
  return Math.round(
    (curve.a * n * level * (2 * n + 1)) / 6 + (curve.b * n * level) / 2 + curve.c * level,
  );
};

const importXp = (curve: ImportCurve): ((level: number) => number) => {
  switch (curve.kind) {
    case "expression":
      return totalsOf(curve);
    case "per_level":
      return (level) => perLevelXp(curve, level);
    case "cumulative":
      return (level) => cumulativeXp(curve.coefficients, curve.round, level);
  }
};

export const importedXpForLevel = (curve: ImportCurve, level: number): number =>
  level <= 0 ? 0 : importXp(curve)(level);

export const importedLevelForXp = (curve: ImportCurve, xp: number): number =>
  !Number.isFinite(xp) || xp <= 0 ? 0 : highestAtMost(MAX_LEVEL, importXp(curve), xp);
